/**
 * Coerce claim/evidence fields to renderable strings so malformed API payloads
 * (e.g. company-rollup origin objects) never hit React error #31.
 */

/**
 * Known report field paths → human labels.
 * Keep in sync with backend `_DIFFABLE_PATHS` / `COMPANY_DIFFABLE_PATHS`.
 */
const CLAIM_FIELD_PATH_LABELS: Record<string, string> = {
  "summary.executiveSummary": "Executive summary",
  "summary.keyChangesSinceLastRun": "Key changes since last run",
  "marketOverview.marketSize": "Market size",
  "marketOverview.marketGrowth": "Market growth",
  "marketOverview.keyApplications": "Key applications",
  "marketOverview.macroTrends": "Macro trends",
  "marketOverview.regulatoryFactors": "Regulatory factors",
  "marketOverview.newsFeed": "Market news",
  "customerAnalysis.existingKeyCustomers": "Existing key customers",
  "customerAnalysis.potentialNewCustomers": "Potential new customers",
  "competitorAnalysis.mainCompetitors": "Main competitors",
  "competitorAnalysis.newEntrants": "New entrants",
  "competitorAnalysis.positionVsCompetition": "Position vs competition",
  "trendAndInnovation.technologyTrends": "Technology trends",
  "trendAndInnovation.businessModelTrends": "Business model trends",
  quantitativeIndicators: "Scores & indicators",
  "risksAndOpportunities.risks": "Risks",
  "risksAndOpportunities.opportunities": "Opportunities",
  strategyMatrix: "Strategy conclusions (TOWS)",
  "recommendedActions.forSegmentOwner": "Recommended actions — segment owner",
  "recommendedActions.forGlobalSalesAdmin": "Recommended actions — sales admin",
  profile: "Company profile",
  recentMoves: "Recent moves",
  companyFit: "Company fit",
  relationship: "Relationship",
  outreach: "Outreach",
}

export function asDisplayString(value: unknown, fallback = ""): string {
  if (value == null) return fallback
  if (typeof value === "string") return value
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  if (typeof value === "object") {
    const record = value as Record<string, unknown>
    if (typeof record.type === "string") return record.type
    if (typeof record.label === "string") return record.label
    if (typeof record.text === "string") return record.text
    if (typeof record.name === "string") return record.name
    return fallback
  }
  return fallback
}

/** Split camelCase / snake_case tokens into a Title-cased phrase. */
function humanizeSegment(segment: string): string {
  const spaced = segment
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
  if (!spaced) return segment
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * Turn a claim `fieldPath` (e.g. `summary.executiveSummary`) into a UI label.
 * Known paths use the same wording as pending-change diffs; unknowns are humanized.
 */
export function claimFieldPathLabel(fieldPath: unknown): string {
  const path = asDisplayString(fieldPath, "").trim()
  if (!path) return "Field"

  const known = CLAIM_FIELD_PATH_LABELS[path]
  if (known) return known

  // Prefix match for dynamic tails like companyClassifications.Philips
  const dot = path.indexOf(".")
  if (dot > 0) {
    const head = path.slice(0, dot)
    const headLabel = CLAIM_FIELD_PATH_LABELS[head]
    if (headLabel) {
      return `${headLabel} · ${humanizeSegment(path.slice(dot + 1))}`
    }
  }

  return path.split(".").map(humanizeSegment).join(" · ")
}

export function claimOriginLabel(origin: unknown): string {
  const raw = asDisplayString(origin, "ai")
  switch (raw) {
    case "ai":
      return "AI"
    case "manual":
      return "Manual"
    case "import":
      return "Import"
    case "system":
      return "System"
    case "company-rollup":
      return "Company roll-up"
    default:
      return raw || "AI"
  }
}

export function claimFreshnessLabel(freshness: unknown): string {
  if (freshness && typeof freshness === "object" && "label" in freshness) {
    return asDisplayString((freshness as { label: unknown }).label, "unknown")
  }
  return asDisplayString(freshness, "unknown")
}
